import { useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useScsService from '../../../../services/ScsService';
import { Game } from '../../../../types/models/Game';
import BookingSectionOccupants from '../../../../pages/public/booking/BookingSectionOccupants';
import BookingSectionPrice from '../../../../pages/public/booking/BookingSectionPrice';
import constants from '../../../../config/constants';
import type { Occupant } from '../../../../types/models/Occupant';
import BookingOperateDialog from '../../../../pages/public/booking/BookingOperateDialog';
import { SttButton } from '../../../../sporttia/all';

interface ActionsProps {
    scId: number;
    game: Game;
    loading: boolean;
    handleBook: (player: Occupant, paymentForm: string) => void;
}

export default function Actions({
    scId,
    game,
    loading,
    handleBook,
}: ActionsProps) {
    const { useGetSc } = useScsService();
    const { t } = useTranslation();

    const [occupants, setOccupants] = useState<Occupant[]>([]);
    const [openOperate, setOpenOperate] = useState(false);
    const [staticBookingMsg] = useState('');

    const { data: sportcenterResponse } = useGetSc(scId);

    const { sc } = useMemo(
        () => ({
            sc: sportcenterResponse?.sc,
        }),
        [sportcenterResponse],
    );

    const price = useMemo(
        () =>
            occupants.reduce<number>((acc: any, occupant: any) => {
                if (occupant.selectedRate) {
                    return acc + Number(occupant.selectedRate.total) || 0;
                }

                return acc;
            }, 0),
        [occupants],
    );

    const handleConfirmPlayer = (
        paymentForm: string | { paymentForm: string },
    ) => {
        const paymentFormValue =
            typeof paymentForm === 'object'
                ? paymentForm.paymentForm
                : paymentForm;

        const player = occupants[0];

        if (!player) return;

        handleBook(player, paymentFormValue);
    };

    if (!sc) return null;

    return (
        <Grid>
            <BookingSectionOccupants
                timeini={game.ini}
                timeend={game.end}
                piece={game}
                column={{ facility: game.facility }}
                sc={sc}
                rateType={constants.rate.types.individual}
                setRateType={undefined}
                ImSCOwner={false}
                occupants={occupants}
                setOccupants={setOccupants}
                isForGame
            />

            <Box mt={2}>
                <BookingSectionPrice
                    price={price}
                    sc={sc}
                    onChangePrice={undefined}
                    modify={false}
                />
            </Box>

            <Box mt={4} mb={2}>
                <SttButton
                    loading={loading}
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        const player = occupants[0];
                        if (!player) return;
                        if (player.selectedBoleto || price <= 0) {
                            handleBook(player, 'FREE');
                        } else {
                            setOpenOperate(true);
                        }
                    }}
                >
                    {t('signupForMatch')}
                </SttButton>
            </Box>

            <BookingOperateDialog
                open={openOperate}
                staticMsg={staticBookingMsg}
                mship={occupants[0]?.mship}
                rateType={constants.rate.types.individual}
                onClose={() => setOpenOperate(false)}
                finalPrice={price}
                onBook={handleConfirmPlayer}
                sc={sc}
                fares={undefined}
            />
        </Grid>
    );
}
